import type { RendererConfig } from '@/engine/types'
import { GraphicsConfigConflictError } from '@/graphics/GraphicsErrors'

/** 引擎支持的图形后端标识。 */
export type GraphicsBackendKind = 'webgl2' | 'webgpu'

/** WebGPU adapter 的标准性能偏好；避免公共类型依赖浏览器是否注入 WebGPU 全局声明。 */
export type WebGPUPowerPreference = 'low-power' | 'high-performance'

/** WebGL2 图形后端配置。 */
export interface WebGL2GraphicsConfig {
  /** 缺省值为 webgl2，保持存量引擎行为。 */
  backend?: 'webgl2'
  /** 未提供旧 pixelRatio 精确覆盖时允许使用的最大设备像素比。 */
  maxPixelRatio?: number
}

/** WebGPU 图形后端配置。 */
export interface WebGPUGraphicsConfig {
  backend: 'webgpu'
  /** 浏览器 adapter 的性能偏好。 */
  powerPreference?: WebGPUPowerPreference
  /** surface 允许使用的最大设备像素比。 */
  maxPixelRatio?: number
  /** 是否启用 WebGPU timestamp-query 调试埋点；默认关闭。 */
  gpuProfiling?: boolean
}

/** 判别式图形后端配置。 */
export type GraphicsConfig = WebGL2GraphicsConfig | WebGPUGraphicsConfig

/** 已补齐默认值并通过基础校验的图形配置。 */
export type ResolvedGraphicsConfig =
  | Readonly<{ backend: 'webgl2'; maxPixelRatio: number }>
  | Readonly<{
      backend: 'webgpu'
      powerPreference?: WebGPUPowerPreference
      maxPixelRatio: number
      gpuProfiling: boolean
    }>

function normalizeMaxPixelRatio(value: number | undefined): number {
  if (value === undefined) {
    return Number.POSITIVE_INFINITY
  }
  if (!Number.isFinite(value) || value <= 0) {
    throw new GraphicsConfigConflictError('graphics.maxPixelRatio 必须是大于 0 的有限数值', [
      'graphics.maxPixelRatio',
    ])
  }
  return value
}

/**
 * 补齐图形配置默认值，并在创建任何 canvas/GPU 对象前拒绝冲突配置。
 *
 * @param graphics - 新图形后端配置
 * @param legacyRenderer - 仅适用于 WebGL2 的旧 renderer 配置
 * @returns 已冻结的配置快照
 */
export function resolveGraphicsConfig(
  graphics?: GraphicsConfig,
  legacyRenderer?: RendererConfig
): ResolvedGraphicsConfig {
  const backend = graphics?.backend ?? 'webgl2'
  if (backend === 'webgpu' && legacyRenderer !== undefined) {
    throw new GraphicsConfigConflictError(
      "graphics.backend='webgpu' 不能与 WebGL-only renderer 配置同时使用",
      ['graphics.backend', 'renderer']
    )
  }

  const maxPixelRatio = normalizeMaxPixelRatio(graphics?.maxPixelRatio)
  if (graphics?.backend === 'webgpu') {
    return Object.freeze({
      backend: 'webgpu',
      powerPreference: graphics.powerPreference,
      maxPixelRatio,
      gpuProfiling: graphics.gpuProfiling === true,
    })
  }
  return Object.freeze({ backend: 'webgl2' as const, maxPixelRatio })
}
